import type { CloudBluePlan } from "@gatsby-plugin-definitions/fragments/CloudBluePlan"
import type { ProductBoxBundle } from "~/fragments/bundle"
import isEmpty from "~/lib/isEmpty"

type AxDrawerOpenArgs = {
  /** Bundle to hand the drawer, or undefined when it must not show a bundle row. */
  bundle: ProductBoxBundle | undefined
  /** Initial checked state of the drawer's bundle checkbox. */
  isBundleSelected: boolean
  /** Plan to open the drawer with instead of the box's own
      `defaultCloudBluePlan`, set only while a swap is in effect. Returning the
      plan itself rather than a boolean keeps the "a swap implies there is a
      bundlePlan" invariant inside this function, where it is tested. */
  mainPlanOverride: CloudBluePlan[] | undefined
}

/**
 * Single source of truth for how the AX drawer is opened from a product box.
 *
 * Two different things are easy to conflate here:
 *  - whether the box is *configured* to swap its main plan (it has both a
 *    bundle plan and an AX-level `bundlePlan`), and
 *  - whether that swap is *currently in effect* (the box's checkbox is ticked).
 *
 * The drawer can't represent a plan swap through its bundle row — ticking a
 * checkbox there would add the plan as a second product instead of replacing
 * the main one — so a swap-configured box passes no bundle at all, ticked or
 * not. While its checkbox is ticked it reports `mainPlanOverride` instead: the
 * plan the drawer should open with in place of the box's own. Unticked, there
 * is nothing to override and the box's own plan stands.
 *
 * An "add alongside" bundle always goes to the drawer so it can be opted into
 * from there; the checkbox merely seeds the initial state.
 */
export default function getAxDrawerOpenArgs(
  bundle: ProductBoxBundle[] | null | undefined,
  bundlePlan: CloudBluePlan[] | null | undefined,
  isBundleChecked: boolean
): AxDrawerOpenArgs {
  const boxBundle = bundle?.[0]

  const isSwappingMainPlan =
    !isEmpty(boxBundle?.flatData.plan) && !isEmpty(bundlePlan)

  if (isSwappingMainPlan) {
    return {
      bundle: undefined,
      isBundleSelected: false,
      mainPlanOverride: isBundleChecked ? bundlePlan : undefined,
    }
  }

  return {
    bundle: boxBundle,
    isBundleSelected: isBundleChecked,
    mainPlanOverride: undefined,
  }
}
